package dev.kuml.portal.web.activity

import dev.kuml.portal.web.api.ActivityDayResponse
import dev.kuml.portal.web.i18n.format.LocaleFormat
import dev.kuml.portal.web.i18n.strings.ActivityStrings
import kotlin.math.ceil

/**
 * Pure, DOM-free logic backing [activitySection]'s GitHub-contribution-graph-style calendar (R-15)
 * -- deliberately separated from [ActivitySection]'s KVision widget tree so it is directly
 * unit-testable under `jsNodeTest` without a browser DOM, mirroring
 * `dev.kuml.portal.web.editor.ZoomMath`/`PngExportMath`/`GenerateState`'s existing split.
 *
 * R-18: [tooltip] and [monthLabels] now take the active language's [ActivityStrings]/[LocaleFormat]
 * as parameters instead of hard-coded German wording/month names (see this wave's plan §5.14,
 * which also fixes two pre-existing copy defects along the way: [tooltip] now shows a properly
 * FORMATTED date via [LocaleFormat.date] instead of the raw ISO string, and the plural rule moved
 * into [ActivityStrings] itself).
 */
public object ActivityCalendar {
    /** Number of colour buckets, 0 (no activity) through [LEVELS] - 1 (busiest). */
    public const val LEVELS: Int = 5

    /**
     * Bucket [count] against [maxCount] into `0 .. LEVELS - 1`. `0` means "no activity at all" --
     * NEVER conflated with "a little activity" (bucket `1`). A [count] equal to [maxCount] always
     * lands in the top bucket (`LEVELS - 1`); [maxCount] `<= 0` is treated as `1` so a fresh
     * account (every day at `0`) never divides by zero.
     */
    public fun level(
        count: Long,
        maxCount: Long,
    ): Int {
        if (count <= 0) return 0
        val effectiveMax = maxOf(1L, maxCount)
        val ratio = count.toDouble() / effectiveMax.toDouble()
        val raw = ceil(ratio * (LEVELS - 1)).toInt()
        return raw.coerceIn(1, LEVELS - 1)
    }

    /** Splits the dense day list into 7-day week columns, first column starting on a Sunday (the
     * server already guarantees [days] starts on a Sunday, see
     * `dev.kuml.portal.server.generations.GenerationActivityService`'s KDoc) -- a plain chunk, no
     * re-alignment needed. The server does NOT round the window's end forward to a Saturday, so
     * the LAST column can be shorter than 7 entries (a partial trailing week ending "today",
     * exactly like GitHub's own contribution graph) -- callers must not assume every week is a
     * full 7 days. */
    public fun weeks(days: List<ActivityDayResponse>): List<List<ActivityDayResponse>> = days.chunked(DAYS_PER_WEEK)

    /** Locale-aware tooltip text for one calendar cell, singular/plural-aware, with a properly
     * FORMATTED date (via [format]) instead of the day's raw ISO date string. */
    public fun tooltip(
        day: ActivityDayResponse,
        strings: ActivityStrings,
        format: LocaleFormat,
    ): String {
        val date = format.date(day.date + "T00:00:00Z") ?: day.date
        return when (day.count) {
            0L -> strings.tooltipNone(date)
            1L -> strings.tooltipOne(date)
            else -> strings.tooltipMany(day.count, date)
        }
    }

    /** Maps a week-column index to a short month label, but ONLY for the first column of each new
     * month -- every other column is absent from the returned map (the caller renders nothing
     * there), exactly like a real contribution-graph header. */
    public fun monthLabels(
        weeks: List<List<ActivityDayResponse>>,
        format: LocaleFormat,
    ): Map<Int, String> {
        val labels = linkedMapOf<Int, String>()
        var lastMonth: Int? = null
        weeks.forEachIndexed { index, week ->
            val firstDay = week.firstOrNull() ?: return@forEachIndexed
            val month =
                firstDay.date
                    .substringAfter('-')
                    .substringBefore('-')
                    .toIntOrNull() ?: return@forEachIndexed
            if (month != lastMonth) {
                labels[index] = format.monthShort(month)
                lastMonth = month
            }
        }
        return labels
    }

    private const val DAYS_PER_WEEK = 7
}
